import { User, useAuth0 } from '@auth0/auth0-react';
import CloudOffOutlined from '@mui/icons-material/CloudOffOutlined';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import LockPersonOutlined from '@mui/icons-material/LockPersonOutlined';
import MarkEmailUnreadOutlined from '@mui/icons-material/MarkEmailUnreadOutlined';
import PersonOffOutlined from '@mui/icons-material/PersonOffOutlined';
import { SvgIconProps, ThemeProvider } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback } from 'react';

import { ErrorProps } from './Error.props';
import {
  ActionRow,
  BodyTypography,
  ErrorCard,
  IconCircle,
  LoginErrorSeverity,
  LogoImage,
  PageWrapper,
  TitleTypography,
} from './Error.styles';
import { Button, ButtonVariantProps } from '../../components/atoms/Button';
import { TypographyVariantProps } from '../../components/atoms/Typography';
import { lightTheme } from '../../configs/themes';
import {
  LOGIN_ERROR_CONTENT,
  LoginErrorType,
  USER_ERROR_SCENARIOS,
} from '../../constants/error.constant';
import { logoutUser } from '../../utils/users';

const SCENARIO_VISUALS: Record<
  LoginErrorType,
  { Icon: React.ComponentType<SvgIconProps>; severity: LoginErrorSeverity }
> = {
  [LoginErrorType.EmailUnverified]: { Icon: MarkEmailUnreadOutlined, severity: 'info' },
  [LoginErrorType.AccountInactive]: { Icon: PersonOffOutlined, severity: 'warning' },
  [LoginErrorType.AccountNotConfigured]: { Icon: LockPersonOutlined, severity: 'warning' },
  [LoginErrorType.ServiceUnavailable]: { Icon: CloudOffOutlined, severity: 'error' },
  [LoginErrorType.SignInFailed]: { Icon: ErrorOutline, severity: 'error' },
};

export const resolveLoginErrorType = (
  user: User | undefined,
  errorMessage: string | undefined,
  provisioningFailed: boolean
): LoginErrorType => {
  if (user?.email_verified === false) {
    return LoginErrorType.EmailUnverified;
  }
  if (user?.blocked === true || errorMessage === USER_ERROR_SCENARIOS.BLOCKED_ERROR) {
    return LoginErrorType.AccountInactive;
  }
  if (errorMessage === USER_ERROR_SCENARIOS.ACCESS_DENIED) {
    return LoginErrorType.AccountNotConfigured;
  }
  if (provisioningFailed) {
    return LoginErrorType.ServiceUnavailable;
  }
  return LoginErrorType.SignInFailed;
};

export const Error: React.FC<ErrorProps> = ({ provisioningFailed = false }) => {
  const { user, error, logout } = useAuth0();
  const queryClient = useQueryClient();

  const errorType = resolveLoginErrorType(user, error?.message, provisioningFailed);
  const { title, message, guidance, canRetry } = LOGIN_ERROR_CONTENT[errorType];
  const { Icon, severity } = SCENARIO_VISUALS[errorType];

  const onTryAgain = useCallback(() => {
    // Full navigation (not an SPA retry) so Auth0 re-runs silent auth with
    // fresh claims — e.g. email_verified flips once the user clicks their link.
    window.location.assign(window.location.origin);
  }, []);

  const onLogOut = useCallback(() => {
    queryClient.clear();
    logoutUser();
    logout({ logoutParams: { returnTo: window.location.origin } });
  }, [logout, queryClient]);

  return (
    <ThemeProvider theme={lightTheme}>
      <PageWrapper>
        <ErrorCard role="alert">
          <LogoImage
            src="/images/leftMenu/Amtek_Logo_Phoenix_Single_Black@0.5x.png"
            alt="Amtek logo"
          />
          <IconCircle severity={severity}>
            <Icon fontSize="large" />
          </IconCircle>
          <TitleTypography variant={TypographyVariantProps.H5} fontWeight={600}>
            {title}
          </TitleTypography>
          {message && (
            <BodyTypography variant={TypographyVariantProps.Body1}>{message}</BodyTypography>
          )}
          {errorType === LoginErrorType.EmailUnverified && user?.email && (
            <TitleTypography variant={TypographyVariantProps.Body1} fontWeight={600}>
              {user.email}
            </TitleTypography>
          )}
          <BodyTypography variant={TypographyVariantProps.Body2}>{guidance}</BodyTypography>
          <ActionRow>
            {canRetry && (
              <Button variant={ButtonVariantProps.Primary} onClick={onTryAgain}>
                Try again
              </Button>
            )}
            <Button
              variant={canRetry ? ButtonVariantProps.Secondary : ButtonVariantProps.Primary}
              onClick={onLogOut}
            >
              Log out
            </Button>
          </ActionRow>
        </ErrorCard>
      </PageWrapper>
    </ThemeProvider>
  );
};
